import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Remounts its subtree whenever the app language changes.
 *
 * Most of this codebase renders text through the scope-independent `tr()`
 * helper rather than the `t` returned by `useTranslation()`. `tr()` reads
 * i18next at call time, which is correct on first paint — but it does NOT
 * subscribe the component to i18next, so nothing re-renders when the language
 * changes. Whatever was already on screen stayed in the previous language until
 * some unrelated state change forced a repaint.
 *
 * That is the mechanism behind CVA-4474 and CVA-4531, and a scan finds 46 files
 * with five or more `tr()` calls and no subscription — so this is a class of bug,
 * not two screens. Keying the tree on the active language fixes all of them at
 * once, and any future file that reaches for `tr()`.
 *
 * Trade-off, deliberately taken: changing language remounts the tree, so
 * in-progress local component state (a half-filled form) is lost. Switching
 * language is a rare, deliberate action where a reset is the expected outcome,
 * and stale mixed-language text is the worse failure. Redux and react-query
 * state live above this boundary and are unaffected.
 *
 * This does NOT fix `tr()` called at module scope — those run once at import,
 * before any of this. Those must be converted to functions individually.
 */
const LanguageBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  return <React.Fragment key={i18n.language}>{children}</React.Fragment>;
};

export default LanguageBoundary;
